"use client";

import { SELLER_VERIFICATION_MUTATION } from "@/gql/availability";
import { UNIT_ONE_QUERY } from "@/gql/unit";
import { useAuth } from "@/hooks/use-auth";
import { COMPANY_INFO } from "@/info/company";
import { useUserStore } from "@/stores/user-store";
import { useMutation, useQuery } from "@apollo/client";
import { deepEqual as isEqualFunc } from "fast-equals";
import { useSearchParams } from "next/navigation";
import React, { useEffect, useMemo, useRef } from "react";
import { useForm, useWatch } from "react-hook-form";
import { toast } from "sonner";
import { Input } from "../rhf/input";
import { PhoneWithCountryField } from "../rhf/phone-with-country-field";
import { ButtonActions } from "./action-buttons";
import { FormProvider, useFormContext } from "./context/FormProvider";
import { useGetBlockData } from "./hooks/useGraphQLQueries";
import { useStepNavigation } from "./hooks/useStepNavigation";
import { getSteps } from "./steps/config";
import { UnitAvailabilityInput } from "./unit-availability-input";
const MemoizedUnitAvailabilityInput = React.memo(UnitAvailabilityInput) as typeof UnitAvailabilityInput;
interface StepSlotBookingData {
  flatNumber: string;
  blockName: string;
  isSelfOccupied: string;
  keyholderName: string;
  keyholderMobile: string;
  availabilitySlots: Array<{
    startTime: Date;
    endTime: Date;
  }>;
  keyholderDesignation: string;
}
interface StepSlotBookingProps {
  onNext: (data: StepSlotBookingData) => void;
  onBack: () => void;
  initialData?: Partial<StepSlotBookingData>;
  isLoading?: boolean;
}

// Custom validation function to replace yup
const validateFormData = (data: StepSlotBookingData, sellerUnitId?: string | null, excludes?: ["blockName"] | null) => {
  const errors: Record<string, string> = {};

  // Validate flat number
  if (!data.flatNumber?.trim()) {
    errors.flatNumber = "Flat number is required";
  } else if (data.flatNumber.length < 1) {
    errors.flatNumber = "Flat number must be at least 1 character";
  } else if (data.flatNumber.length > 50) {
    errors.flatNumber = "Flat number must be less than 50 characters";
  } else if (!/^[a-zA-Z0-9\s\-_\/]+$/.test(data.flatNumber)) {
    errors.flatNumber = "Flat number can only contain letters, numbers, spaces, hyphens, underscores, and forward slashes";
  }

  // Validate block name
  if (!excludes?.includes("blockName") && !data.blockName) {
    errors.blockName = "Please select at least one block";
  }

  // Validate self occupied status
  if (!data.isSelfOccupied) {
    errors.isSelfOccupied = "Please select whether the property is self occupied";
  } else if (!["yes", "no"].includes(data.isSelfOccupied)) {
    errors.isSelfOccupied = "Please select a valid option";
  }

  // Validate keyholder information when not self occupied
  if (data.isSelfOccupied === "no") {
    if (!data.keyholderName?.trim()) {
      errors.keyholderName = "Keyholder name is required when property is not self occupied";
    } else if (data.keyholderName.length < 2) {
      errors.keyholderName = "Keyholder name must be at least 2 characters";
    } else if (data.keyholderName.length > 100) {
      errors.keyholderName = "Keyholder name must be less than 100 characters";
    } else if (!/^[a-zA-Z\s\-']+$/.test(data.keyholderName)) {
      errors.keyholderName = "Keyholder name can only contain letters, spaces, hyphens, and apostrophes";
    }
    if (!data.keyholderMobile?.trim()) {
      errors.keyholderMobile = "Keyholder mobile number is required when property is not self occupied";
    } else if (!/^\+?[1-9]\d{1,14}$/.test(data.keyholderMobile)) {
      errors.keyholderMobile = "Please enter a valid mobile number";
    } else if (data.keyholderMobile.length < 10) {
      errors.keyholderMobile = "Mobile number must be at least 10 digits";
    } else if (data.keyholderMobile.length > 15) {
      errors.keyholderMobile = "Mobile number must be less than 15 digits";
    }
  }

  // Validate availability slots if sellerUnitId exists
  if (sellerUnitId && data.availabilitySlots) {
    if (!Array.isArray(data.availabilitySlots)) {
      errors.availabilitySlots = "Availability slots must be an array";
    } else if (data.availabilitySlots.length === 0) {
      errors.availabilitySlots = "Please select at least one time slot";
    }
  }
  return errors;
};
export function StepSlotBooking({
  onBack,
  initialData,
  isLoading
}: StepSlotBookingProps) {
  const {
    user,
    isAuthenticated
  } = useAuth();
  const {
    state,
    updateStepData
  } = useFormContext();
  const stepsConfig = useMemo(() => state?.profile?.relationship ? getSteps({
    relationship: state.profile.relationship || "owner"
  }) : [], [state]);
  const {
    navigateNext
  } = useStepNavigation(stepsConfig);
  const searchParams = useSearchParams();
  let sellerUnitId = searchParams?.get("sellerUnitId");
  const {
    data: unitData = {}
  } = useQuery(UNIT_ONE_QUERY, {
    variables: {
      filters: {
        one: {
          sellerUnitId
        }
      }
    },
    skip: !isAuthenticated || Boolean(!sellerUnitId)
  });
  const [updateSellerVerification] = useMutation(SELLER_VERIFICATION_MUTATION);
  // Merge initialData with state.slot, but don't mutate the prop
  const [isSubmitting, setIsSubmitting] = React.useState(false);
  const contextSlot = useMemo(() => state.slot as Partial<StepSlotBookingData>, [state.slot]);
  const mergedInitialData = useMemo(() => ({
    ...initialData,
    ...contextSlot
  }), [initialData, contextSlot]);
  const onboardedData = unitData.units?.one;
  // Get the selected group from user preferences store (priority over first group)
  const storeSelectedGroupId = useUserStore(state_0 => state_0.selectedGroupId);
  const sellerGroupId = storeSelectedGroupId || user?.groups?.find(group => group.relationship === "seller")?.id;
  const {
    data: blockData,
    loading: blockLoading,
    error: blockError
  } = useGetBlockData({
    sellerUnitId: sellerUnitId || undefined
  });
  // components/sell/step-8-slot.tsx
  // Default values: context > initialData
  const defaultValues = useMemo(() => ({
    flatNumber: mergedInitialData?.flatNumber || onboardedData?.flatNumber || "",
    blockName: mergedInitialData?.blockName || onboardedData?.block?.name || "",
    isSelfOccupied: mergedInitialData?.isSelfOccupied || onboardedData?.isSelfOccupied || "",
    keyholderName: mergedInitialData?.keyholderName || onboardedData?.keyholderName || "",
    keyholderMobile: mergedInitialData?.keyholderMobile || onboardedData?.keyholderMobile || "",
    availabilitySlots: mergedInitialData?.availabilitySlots || onboardedData?.availabilitySlots || [],
    keyholderDesignation: mergedInitialData?.keyholderDesignation || onboardedData?.keyholderDesignation || ""
  }), [mergedInitialData?.flatNumber, mergedInitialData?.blockName, mergedInitialData?.isSelfOccupied, mergedInitialData?.keyholderName, mergedInitialData?.keyholderMobile, mergedInitialData?.availabilitySlots, mergedInitialData?.keyholderDesignation, onboardedData?.flatNumber, onboardedData?.block?.name, onboardedData?.isSelfOccupied, onboardedData?.keyholderName, onboardedData?.keyholderMobile, onboardedData?.availabilitySlots, onboardedData?.keyholderDesignation]);
  const keyholderDesignationOptions = [{
    label: "Tenant",
    value: "tenant"
  }, {
    label: "Broker",
    value: "broker"
  }, {
    label: "Security Guard",
    value: "security"
  }, {
    label: "Other",
    value: "other"
  }];
  const form = useForm<StepSlotBookingData>({
    defaultValues: defaultValues,
    mode: "onChange"
  });
  const {
    control,
    handleSubmit,
    setValue,
    watch,
    formState,
    reset,
    ...useFormProps
  } = form;

  // Reset if defaults change - but only when we actually have meaningful changes
  const prevDefaults = useRef(defaultValues);
  useEffect(() => {
    // Only reset if we have actual data and it's different from what we had before
    if (Object.keys(mergedInitialData).length > 0 && !isEqualFunc(prevDefaults.current, defaultValues) && (
    // Don't reset if we're just going from empty to empty
    prevDefaults.current.flatNumber || defaultValues.flatNumber || prevDefaults.current.blockName || defaultValues.blockName || prevDefaults.current.isSelfOccupied || defaultValues.isSelfOccupied || prevDefaults.current.keyholderName || defaultValues.keyholderName || prevDefaults.current.keyholderMobile || defaultValues.keyholderMobile || prevDefaults.current.availabilitySlots.length > 0 || defaultValues.availabilitySlots.length > 0)) {
      reset(defaultValues);
      prevDefaults.current = defaultValues;
    }
  }, [defaultValues, reset, mergedInitialData]);

  // Watch form values
  const watchedValues = useWatch({
    control
  }) as StepSlotBookingData;

  // Update context on form change with debounced updates
  const prevWatched = useRef(watchedValues);
  const updateTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  useEffect(() => {
    if (!isEqualFunc(prevWatched.current, watchedValues) && watchedValues) {
      // Clear previous timeout
      if (updateTimeoutRef.current) {
        clearTimeout(updateTimeoutRef.current);
      }
      // Debounce the update to prevent rapid changes
      updateTimeoutRef.current = setTimeout(() => {
        updateStepData("slot", watchedValues);
        prevWatched.current = watchedValues;
      }, 300);
    }

    // Cleanup timeout on unmount
    return () => {
      if (updateTimeoutRef.current) {
        clearTimeout(updateTimeoutRef.current);
      }
    };
  }, [watchedValues, updateStepData]);
  const isSelfOccupied = watch("isSelfOccupied");
  const availabilitySlots = watch("availabilitySlots");

  // Ensure availabilitySlots always contains valid Date objects
  const validAvailabilitySlots = useMemo(() => Array.isArray(availabilitySlots) ? availabilitySlots.filter(slot => !isNaN(new Date(slot.startTime).getTime()) && !isNaN(new Date(slot.endTime).getTime())) : [], [availabilitySlots]);
  const options = (blockData?.blocks?.list?.map((block: {
    name: string;
    id: string;
  }) => ({
    label: block.name,
    value: block.id
  })) || []).sort((a: {
    label: string;
  }, b: {
    label: string;
  }) => a.label.localeCompare(b.label));

  // Form validation function
  const validateForm = React.useCallback((data: StepSlotBookingData, excludes?: ["blockName"]): boolean => {
    const errors = validateFormData(data, sellerUnitId, excludes);
    if (Object.keys(errors).length > 0) {
      // Show first error
      const firstError = Object.values(errors)[0];
      toast.error(firstError);
      return false;
    }
    return true;
  }, [sellerUnitId]);

  // Submit availability slots to the server
  const hSubmit = React.useCallback(async () => {
    if (validAvailabilitySlots.length === 0) {
      toast.error("Please select at least one time slot");
      return;
    }
    if (!sellerGroupId) {
      toast.error("Seller information not found. Please try logging in again.");
      return;
    }
    let blockOpt = options.find((option: {
      value: string;
    }) => watchedValues.blockName === option.value);
    if (!blockOpt && watchedValues.blockName.length > 0) {
      blockOpt = {
        value: watchedValues.blockName,
        label: watchedValues.blockName
      };
    }
    setIsSubmitting(true);
    try {
      const result = await updateSellerVerification({
        variables: {
          params: {
            sellerVerificationRequest: {
              slots: validAvailabilitySlots,
              sellerUnitId: sellerUnitId,
              isMobile: false,
              info: {
                flatNumber: watchedValues.flatNumber,
                ...(blockOpt && {
                  block: {
                    id: blockOpt.value,
                    name: blockOpt.label
                  }
                }),
                isSelfOccupied: watchedValues.isSelfOccupied === "yes" ? true : false,
                ...(watchedValues.isSelfOccupied === "no" ? {
                  keyholderName: watchedValues.keyholderName,
                  keyholderMobile: watchedValues.keyholderMobile,
                  keyholderDesignation: watchedValues.keyholderDesignation
                } : {})
              }
            }
          }
        }
      });
      return result;
    } catch (err) {
      toast.error("Failed to update availability");
      throw err; // Re-throw to be handled by caller
    } finally {
      setIsSubmitting(false);
    }
  }, [sellerGroupId, sellerUnitId, validAvailabilitySlots, watchedValues, options, updateSellerVerification]);
  const onSubmit = React.useCallback((data_0: StepSlotBookingData) => {
    // Validate form before proceeding
    if (!validateForm(data_0, options.length === 0 ? ["blockName"] : undefined)) {
      return;
    }

    // Ensure the data has valid availability slots before proceeding
    // const dataWithValidSlots = {
    // 	...data,
    // 	availabilitySlots: validAvailabilitySlots,
    // };

    if (sellerUnitId) {
      hSubmit().then(result_0 => {
        if ((result_0 as any)?.data?.seller?.sellerVerificationRequest) {
          navigateNext();
        } else if ((result_0 as any)?.errors) {
          toast.error("Failed to update availability");
        }
      }).catch(err_0 => {
        toast.error("Failed to update availability", {
          description: err_0.message
        });
      });
    } else {
      navigateNext();
    }
  }, [validateForm, options.length, sellerUnitId, hSubmit, navigateNext]);
  if (!sellerUnitId) return <div className="p-4 text-center text-red-500">
				No unit exists. Kindly contact to RM on this number: {COMPANY_INFO.contact.phone.primary}{" "}
			</div>;
  const shouldShowBlockField = Boolean(blockLoading || options.length > 0);
  return <FormProvider {...{
    control,
    handleSubmit,
    setValue,
    watch,
    formState,
    reset,
    ...useFormProps
  }}>
			<form onSubmit={handleSubmit(onSubmit)} className="space-y-6 mx-auto p-2">
				{/* Flat and Block Number */}
				<div className="grid grid-cols-1 md:grid-cols-2 gap-4">
					<Input label="Flat Name" name="flatNumber" type="text" control={control} placeholder="Enter flat number" />

					{shouldShowBlockField && <Input className="py-0! gap-1!" label="Block Name" name="blockName" type="select2" control={control} placeholder="Enter block name" options={blockLoading ? [{
          label: "Option 1",
          value: defaultValues.blockName || ""
        }] : options} inputProps={{
          className: "h-10!"
        }} creatable createOption={(value: string) => ({
          label: value,
          value: value
        })} key={blockLoading ? "loading" : "loaded"} />}
				</div>

				{/* Self Occupied Status */}
				<div className="space-y-4 grid grid-cols-1 md:grid-cols-2 gap-4">
					<Input type="buttongroup" label="Is property self occupied?" name="isSelfOccupied" options={[{
          label: "Yes",
          value: "yes"
        }, {
          label: "No",
          value: "no"
        }]} control={control} />
					{isSelfOccupied === "no" && <Input label="Name of Keyholder" name="keyholderName" type="text" control={control} placeholder="Enter keyholder name" />}
				</div>

				{/* Keyholder Information - Only show if not self occupied */}
				{isSelfOccupied === "no" && <div className="space-y-4 grid grid-cols-1 md:grid-cols-2 gap-4">
						<PhoneWithCountryField label="Mobile of Keyholder" name="keyholderMobile" control={control} phonePlaceholder="Enter keyholder mobile number" countryPlaceholder="Select keyholder country" />
						<Input className="py-0! gap-1!" label="Keyholder Designation" name="keyholderDesignation" type="select2" control={control} placeholder="Enter keyholder designation" options={keyholderDesignationOptions} inputProps={{
          className: "h-10!"
        }} />
					</div>}

				{/* Unit Availability Calendar  */}
				<div className="space-y-3">
					<label className="text-title-medium">Select Available Time Slots</label>
					<div className="w-full my-1">
						<MemoizedUnitAvailabilityInput name="availabilitySlots" control={control} sellerUnitId={sellerUnitId} />
					</div>
				</div>

				{/* Block data error state */}
				{blockError && <div className="p-4 text-center text-red-500">Error loading block information. Please refresh the page.</div>}
				{/* Action Buttons */}
				<ButtonActions next isLoading={isLoading || isSubmitting} disableBack />
			</form>
		</FormProvider>;
}